import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { TablePagination, usePagination } from "@/components/TablePagination";
import { format, parseISO, isWithinInterval, startOfDay, endOfDay, parse } from "date-fns";
import { DocumentRecord, EstadoDocumento, InventoryItem } from "@/hooks/useDocuments";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";

import { TableToolbar, exportToCSV, exportToExcel, DateRange } from "@/components/TableToolbar";
import { Button } from "@/components/ui/button";
import { Eye, Pencil, Trash2, Check, X, ClipboardCheck, Ship, Package } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { DocumentViewerModal } from "@/components/DocumentViewerModal";
import { AuditPanel } from "@/components/AuditPanel";
import { Input } from "@/components/ui/input";
import { EditableCell } from "@/components/ui/EditableCell";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { isNonFinancialType, MASTER_DOC_TYPES } from "@/lib/documentTypes";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuditTrail } from "@/hooks/useAuditTrail";
import { ResizableTableHead } from "@/components/ResizableTableHead";
import { useIsMobile } from "@/hooks/use-mobile";
import { TruncatedCell } from "@/components/TruncatedCell";
import { DocumentCardMobile } from "@/components/DocumentCardMobile";
import { EditDocumentDrawer } from "@/components/EditDocumentDrawer";
import { calcHeaderTotals } from "@/hooks/useInvoiceMath";
import { EmbarqueDetailModal } from "@/components/EmbarqueDetailModal";
import { EmbarqueSelectField } from "@/components/EmbarqueSelectField";
import { CurrencyQuickFix } from "@/components/CurrencyQuickFix";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ESTADOS: EstadoDocumento[] = ["Pendiente", "Procesando", "Pagado", "Completado", "Cancelado"];

const ESTADO_STYLES: Record<EstadoDocumento, string> = {
  Pendiente: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30",
  Procesando: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
  Pagado: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30",
  Completado: "bg-primary/15 text-primary border-primary/30",
  Cancelado: "bg-destructive/15 text-destructive border-destructive/30",
};

interface Props {
  documents: DocumentRecord[];
  loading?: boolean;
  selectedDocumentId?: string | null;
  onSelectDocument?: (id: string | null) => void;
  documentType?: string;
  onFilteredIdsChange?: (ids: string[]) => void;
  inventoryItems?: InventoryItem[];
  disableEmbarqueDetail?: boolean;
  /** IDs de documentos a resaltar temporalmente (deep-link desde banner). */
  highlightIds?: string[];
  /** Si se provee, el click en la fila ejecuta este handler en vez del toggle de selección/filtro. */
  onRowClick?: (doc: DocumentRecord) => void;
  /** Oculta la columna "Auditoría" (cuando el row-click ya abre el AuditPanel). */
  hideAuditColumn?: boolean;
}

type ColumnProfile = "financial" | "packing" | "minimal" | "all";

function getColumnProfile(documentType?: string): ColumnProfile {
  if (!documentType) return "all";
  if (documentType.toLowerCase() === "packing list") return "packing";
  return "financial";
}

const EDITABLE_FIELDS = [
  "fecha", "tipo_documento", "no_documento", "emisor", "receptor",
  "moneda", "costo_flete", "seguro", "impuestos", "descuento_ajuste", "otros_cargos", "origen", "destino",
  "incoterm", "notas_tecnicas",
] as const;

const LOCKED_FIELDS = ["total_factura", "total_piezas", "peso_bruto_kg"] as const;

type EditableField = typeof EDITABLE_FIELDS[number];

function formatCreatedAt(dateStr: string): string {
  try {
    return format(new Date(dateStr), "dd/MM/yyyy HH:mm");
  } catch {
    return dateStr;
  }
}

/** Pildora compacta de logística con HoverCard de desglose. */
function LogisticsHoverPill({ doc }: { doc: DocumentRecord }) {
  const piezas = doc.total_piezas;
  const bruto = doc.peso_bruto_kg;
  const neto = doc.peso_neto_kg;
  const cbm = doc.volumen_cbm;
  const hasAny = [piezas, bruto, neto, cbm].some(v => v != null && Number(v) > 0);

  const fmt = (v: number | null | undefined, digits = 2) =>
    v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const fmtInt = (v: number | null | undefined) =>
    v == null ? "—" : Number(v).toLocaleString();

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-mono tabular-nums transition-colors",
            hasAny
              ? "border-primary/30 bg-primary/5 text-foreground hover:bg-primary/10"
              : "border-dashed border-muted-foreground/30 text-muted-foreground hover:bg-muted/40"
          )}
          aria-label="Ver detalle logístico"
        >
          <Package className="h-3 w-3" />
          {hasAny ? `${fmtInt(bruto != null ? Math.round(Number(bruto)) : null)} kg` : "Sin datos"}
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="left" align="start" className="w-64 p-0">
        <div className="border-b border-border px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Datos Logísticos</p>
          <p className="text-[10px] text-muted-foreground/80 truncate">{doc.no_documento}</p>
        </div>
        <div className="px-3 py-2 space-y-1.5 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Total Piezas</span>
            <span className="font-mono tabular-nums text-foreground">{fmtInt(piezas)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Peso Bruto</span>
            <span className="font-mono tabular-nums text-foreground">{fmt(bruto)} kg</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Peso Neto</span>
            <span className={cn(
              "font-mono tabular-nums",
              neto != null && bruto != null && Number(neto) > Number(bruto) ? "text-amber-600 dark:text-amber-400" : "text-foreground"
            )}>{fmt(neto)} kg</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">Volumen</span>
            <span className="font-mono tabular-nums text-foreground">{fmt(cbm, 3)} CBM</span>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function MobileCardList({
  filtered, selectedDocumentId, onSelectDocument, setViewerUrl, setViewerTitle,
  setDeleteId, updateEstado, showFinancial, showPacking, inventoryCountMap, onUpdateDescuento, inventoryItems,
}: {
  filtered: DocumentRecord[];
  selectedDocumentId?: string | null;
  onSelectDocument?: (id: string | null) => void;
  setViewerUrl: (url: string | null) => void;
  setViewerTitle: (title: string) => void;
  setDeleteId: (id: string | null) => void;
  updateEstado: (id: string, estado: EstadoDocumento) => void;
  showFinancial: boolean;
  showPacking: boolean;
  inventoryCountMap: Record<string, number>;
  onUpdateDescuento: (id: string, descuento: number) => void;
  inventoryItems: InventoryItem[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [editDoc, setEditDoc] = useState<DocumentRecord | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (selectedDocumentId) {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedDocumentId]);

  const visibleDocs = selectedDocumentId
    ? filtered.filter((doc) => doc.id === selectedDocumentId)
    : filtered;

  const handleEdit = (doc: DocumentRecord) => {
    setEditDoc(doc);
    setEditOpen(true);
  };

  return (
    <div ref={containerRef} className="p-3 space-y-3">
      {visibleDocs.map((doc) => (
        <DocumentCardMobile
          key={doc.id}
          doc={doc}
          isSelected={selectedDocumentId === doc.id}
          isFocused={selectedDocumentId === doc.id}
          onSelect={(id) => onSelectDocument?.(id)}
          onViewPdf={(url, title) => { setViewerUrl(url); setViewerTitle(title); }}
          onEdit={handleEdit}
          onDelete={(id) => setDeleteId(id)}
          onUpdateEstado={updateEstado}
          onUpdateDescuento={onUpdateDescuento}
          showFinancial={showFinancial}
          showPacking={showPacking}
          inventoryCount={inventoryCountMap[doc.id] || 0}
        />
      ))}
      {filtered.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-8">No se encontraron documentos</p>
      )}
      <EditDocumentDrawer doc={editDoc} open={editOpen} onOpenChange={setEditOpen} inventoryItems={inventoryItems} documentType={editDoc?.tipo_documento} />
    </div>
  );
}

export function DocumentsTable({ documents, loading, selectedDocumentId, onSelectDocument, documentType, onFilteredIdsChange, inventoryItems = [], disableEmbarqueDetail = false, highlightIds, onRowClick, hideAuditColumn = false }: Props) {

  const highlightSet = useMemo(() => new Set(highlightIds ?? []), [highlightIds]);
  const firstHighlightRef = useRef<HTMLTableRowElement | null>(null);

  // Auto-scroll al primer documento resaltado cuando cambia el set.
  useEffect(() => {
    if (highlightSet.size > 0 && firstHighlightRef.current) {
      firstHighlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightSet]);


  const [search, setSearch] = useState("");
  const [fechaSubidaRange, setFechaSubidaRange] = useState<DateRange>({});
  const [fechaRange, setFechaRange] = useState<DateRange>({});
  const [estadoFilter, setEstadoFilter] = useState("");
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [auditDoc, setAuditDoc] = useState<DocumentRecord | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [embarqueDetailId, setEmbarqueDetailId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { logChange } = useAuditTrail();
  const { currentPage, rowsPerPage, setCurrentPage, handleRowsPerPageChange, resetPage, paginate } = usePagination(20);

  const isMobile = useIsMobile();
  const profile = getColumnProfile(documentType);
  const showFinancial = profile === "financial" || profile === "all";
  const showPacking = profile === "packing";
  const showMinimal = profile === "minimal";

  // Build inventory count map for mobile focus mode hint
  const inventoryCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of inventoryItems) {
      map[item.documento_id] = (map[item.documento_id] || 0) + 1;
    }
    return map;
  }, [inventoryItems]);

  const filtered = useMemo(() => {
    return documents.filter(d => {
      // Text search
      const matchesSearch = d.no_documento.toLowerCase().includes(search.toLowerCase()) ||
        (d.emisor || "").toLowerCase().includes(search.toLowerCase());
      if (!matchesSearch) return false;

      // Fecha Subida filter
      if (fechaSubidaRange.from || fechaSubidaRange.to) {
        try {
          const created = new Date(d.created_at);
          if (fechaSubidaRange.from && created < startOfDay(fechaSubidaRange.from)) return false;
          if (fechaSubidaRange.to && created > endOfDay(fechaSubidaRange.to)) return false;
        } catch { return false; }
      }

      // Fecha filter
      if (fechaRange.from || fechaRange.to) {
        if (!d.fecha) return false;
        try {
          // Try parsing common date formats
          let fecha: Date;
          if (d.fecha.includes("T") || d.fecha.includes("-")) {
            fecha = parseISO(d.fecha);
          } else {
            fecha = parse(d.fecha, "dd/MM/yyyy", new Date());
          }
          if (isNaN(fecha.getTime())) return false;
          if (fechaRange.from && fecha < startOfDay(fechaRange.from)) return false;
          if (fechaRange.to && fecha > endOfDay(fechaRange.to)) return false;
        } catch { return false; }
      }

      // Estado filter
      if (estadoFilter && (d.estado || "Pendiente") !== estadoFilter) return false;

      return true;
    });
  }, [documents, search, fechaSubidaRange, fechaRange, estadoFilter]);

  // Reset page when filters change
  useEffect(() => { resetPage(); }, [search, fechaSubidaRange, fechaRange, estadoFilter]);

  const paginatedDocs = useMemo(() => paginate(filtered), [filtered, currentPage, rowsPerPage]);

  // Report filtered IDs to parent for cascading filters
  useEffect(() => {
    onFilteredIdsChange?.(filtered.map(d => d.id));
  }, [filtered, onFilteredIdsChange]);

  const updateEstado = async (docId: string, estado: EstadoDocumento) => {
    const { error } = await supabase
      .from("registro_documentos")
      .update({ estado } as any)
      .eq("id", docId);
    if (error) {
      toast({ title: "Error al actualizar estado", description: error.message, variant: "destructive" });
    } else {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["all-documents"] });
      queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
    }
  };

  const getExportData = () => {
    const headers: string[] = ["Fecha Subida", "Fecha", "Tipo", "No Documento", "Emisor", "Receptor"];
    const numericCols: number[] = [];
    if (showFinancial) {
      headers.push("Moneda", "Total", "Flete", "Seguro", "Impuestos", "Descuento", "Otros Cargos", "Incoterm");
      numericCols.push(headers.indexOf("Total"), headers.indexOf("Flete"), headers.indexOf("Seguro"), headers.indexOf("Impuestos"), headers.indexOf("Descuento"), headers.indexOf("Otros Cargos"));
    }
    if (showPacking) {
      headers.push("Total Piezas", "Peso Bruto (KG)", "Volumen (CBM)");
      numericCols.push(headers.indexOf("Total Piezas"), headers.indexOf("Peso Bruto (KG)"), headers.indexOf("Volumen (CBM)"));
    }
    if (!showMinimal) headers.push("Origen", "Destino");
    headers.push("Estado", "Notas");

    const rows = filtered.map(d => {
      const row: (string | number)[] = [formatCreatedAt(d.created_at), d.fecha || "", d.tipo_documento || "", d.no_documento, d.emisor || "", d.receptor || ""];
      if (showFinancial) row.push(d.moneda || "", d.total_factura ?? 0, d.costo_flete ?? 0, d.seguro ?? 0, d.impuestos ?? 0, d.descuento_ajuste ?? 0, d.otros_cargos ?? 0, d.incoterm || "");
      if (showPacking) row.push(d.total_piezas ?? 0, d.peso_bruto_kg ?? 0, d.volumen_cbm ?? 0);
      if (!showMinimal) row.push(d.origen || "", d.destino || "");
      row.push(d.estado || "", d.notas_tecnicas || "");
      return row;
    });
    return { headers, rows, numericCols };
  };

  const handleExportCSV = () => {
    const { headers, rows } = getExportData();
    exportToCSV(headers, rows.map(r => r.map(String)), "documentos.csv");
  };

  const handleExportExcel = async () => {
    try {
      const { headers, rows, numericCols } = getExportData();
      await exportToExcel(headers, rows, "documentos.xlsx", numericCols);
    } catch (err) {
      console.error("Error exportando Excel:", err);
    }
  };

  const startEdit = (doc: DocumentRecord) => {
    setEditingId(doc.id);
    const values: Record<string, string> = {};
    for (const f of EDITABLE_FIELDS) {
      values[f] = String(doc[f] ?? "");
    }
    values["embarque_id"] = doc.embarque_id || "";
    setEditValues(values);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = async () => {
    if (!editingId) return;

    // ── Freeze old values BEFORE building update ──
    const editedDoc = documents.find(d => d.id === editingId);
    const CONGELADO_OLD: Record<string, string> = {};
    for (const f of EDITABLE_FIELDS) {
      CONGELADO_OLD[f] = String((editedDoc as any)?.[f] ?? "");
    }
    CONGELADO_OLD["embarque_id"] = String(editedDoc?.embarque_id ?? "");

    const update: Record<string, any> = {};
    for (const f of EDITABLE_FIELDS) {
      if (["total_factura", "costo_flete", "seguro", "impuestos", "descuento_ajuste", "otros_cargos"].includes(f)) {
        update[f] = parseFloat(editValues[f]) || 0;
      } else {
        update[f] = editValues[f] || null;
      }
    }
    update["embarque_id"] = editValues["embarque_id"] || null;
    // Recalculate locked fields from inventory + edited header values
    const siblings = inventoryItems.filter(i => i.documento_id === editingId);
    const flete = parseFloat(editValues["costo_flete"]) || 0;
    const seg = parseFloat(editValues["seguro"]) || 0;
    const imp = parseFloat(editValues["impuestos"]) || 0;
    const desc = parseFloat(editValues["descuento_ajuste"]) || 0;
    const otros = parseFloat(editValues["otros_cargos"]) || 0;
    const totals = calcHeaderTotals(siblings, flete, imp, desc, seg, otros);
    update["total_factura"] = totals.total_factura;
    update["total_piezas"] = totals.total_piezas;
    update["peso_bruto_kg"] = totals.peso_bruto_kg;

    const { error } = await supabase
      .from("registro_documentos")
      .update(update)
      .eq("id", editingId);

    if (error) {
      toast({ title: "Error al guardar", description: error.message, variant: "destructive" });
    } else {
      // ── Audit trail: log each changed field ──
      const auditFields = [...EDITABLE_FIELDS, "embarque_id"];
      for (const f of auditFields) {
        const oldVal = (CONGELADO_OLD[f] ?? "").trim();
        const newVal = String(update[f] ?? "").trim();
        if (oldVal !== newVal) {
          await logChange(editingId, f, oldVal, newVal);
        }
      }
      toast({ title: "✅ Documento actualizado" });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["all-documents"] });
      queryClient.invalidateQueries({ queryKey: ["document-types"] });
      queryClient.invalidateQueries({ queryKey: ["embarques"] });
      queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
    }
    cancelEdit();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.rpc('soft_delete_document', { p_doc_id: deleteId });

    if (error) {
      toast({ title: "Error al archivar", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "📦 Documento archivado" });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["all-documents"] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
      queryClient.invalidateQueries({ queryKey: ["embarques"] });
    }
    setDeleteId(null);
  };

  // Compute derived total for the row being edited (live preview)
  const editDerivedTotal = useMemo(() => {
    if (!editingId) return null;
    const doc = documents.find(d => d.id === editingId);
    if (!doc) return null;
    // Get sibling inventory items for this doc
    const siblings = inventoryItems.filter(i => i.documento_id === editingId);
    const flete = parseFloat(editValues["costo_flete"]) || 0;
    const seg = parseFloat(editValues["seguro"]) || 0;
    const imp = parseFloat(editValues["impuestos"]) || 0;
    const desc = parseFloat(editValues["descuento_ajuste"]) || 0;
    const otros = parseFloat(editValues["otros_cargos"]) || 0;
    return calcHeaderTotals(siblings, flete, imp, desc, seg, otros);
  }, [editingId, editValues, inventoryItems, documents]);

  // ── Inline cell editing for financial fields ──
  const INLINE_DOC_FIELDS: EditableField[] = ["costo_flete", "seguro", "impuestos", "descuento_ajuste", "otros_cargos", "no_documento"];

  const handleDocInlineSave = useCallback(async (doc: DocumentRecord, field: EditableField, rawValue: string) => {
    const CONGELADO_OLD_VALUE = String((doc as any)[field] ?? "");

    const isNumeric = ["costo_flete", "seguro", "impuestos", "descuento_ajuste", "otros_cargos"].includes(field);
    const dbValue = isNumeric ? (parseFloat(rawValue.replace(/[^0-9.\-]/g, "")) || 0) : (rawValue.trim() || null);
    const update: Record<string, any> = { [field]: dbValue };

    // If a financial field changed, recalc totals
    if (isNumeric) {
      const siblings = inventoryItems.filter(i => i.documento_id === doc.id);
      const vals: Record<string, number> = {
        costo_flete: parseFloat(String(doc.costo_flete)) || 0,
        seguro: parseFloat(String(doc.seguro)) || 0,
        impuestos: parseFloat(String(doc.impuestos)) || 0,
        descuento_ajuste: parseFloat(String(doc.descuento_ajuste)) || 0,
        otros_cargos: parseFloat(String(doc.otros_cargos)) || 0,
      };
      vals[field] = dbValue as number;
      const totals = calcHeaderTotals(siblings, vals.costo_flete, vals.impuestos, vals.descuento_ajuste, vals.seguro, vals.otros_cargos);
      update.total_factura = totals.total_factura;
      update.total_piezas = totals.total_piezas;
      update.peso_bruto_kg = totals.peso_bruto_kg;
    }

    const { error } = await supabase.from("registro_documentos").update(update).eq("id", doc.id);
    if (error) throw error;

    // Audit trail after successful DB write
    await logChange(doc.id, field, CONGELADO_OLD_VALUE, String(dbValue ?? ""));

    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["all-documents"] });
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
    toast({ title: "✅ Campo actualizado" });
  }, [inventoryItems, queryClient, toast, logChange]);

  const renderInlineDocCell = (doc: DocumentRecord, field: EditableField, display: React.ReactNode) => {
    if (editingId === doc.id) return renderCell(doc, field, display);
    if (!INLINE_DOC_FIELDS.includes(field)) return display;
    const isNumeric = ["costo_flete", "seguro", "impuestos", "descuento_ajuste", "otros_cargos"].includes(field);
    return (
      <EditableCell
        value={(doc as any)[field]}
        type={isNumeric ? "number" : "text"}
        displayFormat={() => display}
        onSave={async (val) => handleDocInlineSave(doc, field, val)}
        disabled={!!editingId}
      />
    );
  };

  const renderCell = (doc: DocumentRecord, field: EditableField, display?: React.ReactNode) => {
    if (editingId === doc.id) {
      if (field === "tipo_documento") {
        const TIPO_OPTIONS = MASTER_DOC_TYPES.filter(t => t !== "Otro");
        const currentVal = editValues[field] ?? "";
        const isOtro = currentVal !== "" && !(TIPO_OPTIONS as readonly string[]).includes(currentVal) && currentVal !== "Otro";
        const selectVal = isOtro ? "Otro" : (currentVal || "__none__");
        return (
          <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
            <Select
              value={selectVal}
              onValueChange={(v) => setEditValues(prev => ({ ...prev, [field]: v === "__none__" ? "" : v }))}
            >
              <SelectTrigger className="h-7 text-xs w-full">
                <SelectValue placeholder="Seleccionar…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin tipo</SelectItem>
                {TIPO_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
                <SelectItem value="Otro">Otro</SelectItem>
              </SelectContent>
            </Select>
            {(selectVal === "Otro") && (
              <Input
                value={editValues[field] === "Otro" ? "" : editValues[field] ?? ""}
                onChange={(e) => setEditValues(prev => ({ ...prev, [field]: e.target.value || "Otro" }))}
                placeholder="Especificar…"
                className="h-7 text-xs w-full"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        );
      }
      return (
        <Input
          value={editValues[field] ?? ""}
          onChange={(e) => setEditValues(prev => ({ ...prev, [field]: e.target.value }))}
          className="h-7 text-xs min-w-[80px]"
        />
      );
    }
    return display ?? (doc[field] as string) ?? "";
  };

  const thClass = "text-xs text-muted-foreground font-semibold";

  return (
    <div className="rounded-xl overflow-hidden bg-card border border-border shadow-sm">
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">Registro de Documentos</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{loading ? "Cargando..." : `${filtered.length} registros`}</p>
      </div>
      <TableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        onExportCSV={handleExportCSV}
        onExportExcel={handleExportExcel}
        fechaSubidaRange={fechaSubidaRange}
        onFechaSubidaChange={setFechaSubidaRange}
        fechaRange={fechaRange}
        onFechaChange={setFechaRange}
        estadoFilter={estadoFilter}
        onEstadoChange={setEstadoFilter}
      />
      {isMobile ? (
        <MobileCardList
          filtered={filtered}
          selectedDocumentId={selectedDocumentId}
          onSelectDocument={onSelectDocument}
          setViewerUrl={setViewerUrl}
          setViewerTitle={setViewerTitle}
          
          setDeleteId={setDeleteId}
          updateEstado={updateEstado}
          showFinancial={showFinancial}
          showPacking={showPacking}
          inventoryCountMap={inventoryCountMap}
          onUpdateDescuento={async (id, descuento) => {
            const doc = documents.find(d => d.id === id);
            const CONGELADO_OLD_DESCUENTO = String(doc?.descuento_ajuste ?? "0");
            const siblings = inventoryItems.filter(i => i.documento_id === id);
            const flete = parseFloat(String(doc?.costo_flete)) || 0;
            const seg = parseFloat(String(doc?.seguro)) || 0;
            const imp = parseFloat(String(doc?.impuestos)) || 0;
            const otros = parseFloat(String(doc?.otros_cargos)) || 0;
            const totals = calcHeaderTotals(siblings, flete, imp, descuento, seg, otros);
            const { error } = await supabase.from("registro_documentos").update({
              descuento_ajuste: descuento,
              total_factura: totals.total_factura,
              total_piezas: totals.total_piezas,
              peso_bruto_kg: totals.peso_bruto_kg,
            }).eq("id", id);
            if (error) {
              toast({ title: "Error al guardar descuento", description: error.message, variant: "destructive" });
            } else {
              await logChange(id, "descuento_ajuste", CONGELADO_OLD_DESCUENTO, String(descuento));
              queryClient.invalidateQueries({ queryKey: ["documents"] });
              queryClient.invalidateQueries({ queryKey: ["all-documents"] });
              queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
            }
          }}
          inventoryItems={inventoryItems}
        />
      ) : (
        <div
          className="w-full overflow-x-auto rounded-md border pb-2"
          onWheel={(e) => {
            if (e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.scrollLeft += e.deltaY;
            }
          }}
        >
          <div className="min-w-[900px]">
            <Table style={{ tableLayout: "fixed" }}>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <ResizableTableHead className={thClass} initialWidth={130} minWidth={90}>Fecha Subida</ResizableTableHead>
                  <ResizableTableHead className={thClass} initialWidth={90} minWidth={60}>Fecha</ResizableTableHead>
                  {!showMinimal && (
                    <ResizableTableHead className={thClass} initialWidth={150} minWidth={100}>Tipo</ResizableTableHead>
                  )}
                  <ResizableTableHead className={thClass} initialWidth={110} minWidth={70}>No Documento</ResizableTableHead>
                  <ResizableTableHead className={thClass} initialWidth={140} minWidth={90}>Embarque</ResizableTableHead>
                  <ResizableTableHead className={thClass} initialWidth={120} minWidth={70}>Emisor</ResizableTableHead>
                  <ResizableTableHead className={thClass} initialWidth={120} minWidth={70}>Receptor</ResizableTableHead>
                  {!showPacking && (
                    <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={100} minWidth={80}>Logística</ResizableTableHead>
                  )}
                  {showFinancial && (
                     <>
                       {/* TSK-205: Columnas Flete/Seguro/Impuestos/Descuento/Otros eliminadas — ahora viven en gastos_embarque (Bolsa de Gastos Globales V2). Edición disponible vía drawer. */}
                       <ResizableTableHead className={thClass} initialWidth={70} minWidth={50}>Moneda</ResizableTableHead>
                       <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={110} minWidth={70}>Total Factura (FOB)</ResizableTableHead>
                     </>
                  )}
                  {showPacking && (
                    <>
                      <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={100} minWidth={60}>Total Piezas</ResizableTableHead>
                      <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={100} minWidth={60}>Peso Bruto (KG)</ResizableTableHead>
                      <ResizableTableHead className={cn(thClass, "text-right")} initialWidth={100} minWidth={60}>Volumen (CBM)</ResizableTableHead>
                    </>
                  )}
                  {!showMinimal && (
                    <>
                      <ResizableTableHead className={thClass} initialWidth={90} minWidth={60}>Origen</ResizableTableHead>
                      <ResizableTableHead className={thClass} initialWidth={90} minWidth={60}>Destino</ResizableTableHead>
                    </>
                  )}
                  {showFinancial && (
                    <ResizableTableHead className={thClass} initialWidth={80} minWidth={50}>Incoterm</ResizableTableHead>
                  )}
                  <ResizableTableHead className={thClass} initialWidth={120} minWidth={70}>Notas</ResizableTableHead>
                  {!hideAuditColumn && (
                    <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={80} minWidth={60}>Auditoría</ResizableTableHead>
                  )}
                  <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={110} minWidth={80}>Estado</ResizableTableHead>
                  <ResizableTableHead className={cn(thClass, "text-center")} initialWidth={100} minWidth={70}>Acciones</ResizableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedDocs.map((doc) => {
                  const isHighlighted = highlightSet.has(doc.id);
                  const isFirstHighlight = isHighlighted && doc.id === paginatedDocs.find(d => highlightSet.has(d.id))?.id;
                  return (
                  <TableRow
                    key={doc.id}
                    ref={isFirstHighlight ? firstHighlightRef : undefined}
                    className={cn(
                      "border-border transition-colors cursor-pointer",
                      selectedDocumentId === doc.id
                        ? "bg-primary/15 hover:bg-primary/20 border-l-[3px] border-l-primary"
                        : isHighlighted
                          ? "bg-amber-100/70 dark:bg-amber-950/40 ring-2 ring-inset ring-amber-500/60 animate-pulse"
                          : "hover:bg-muted/30"
                    )}
                    onClick={() => {
                      if (onRowClick) { onRowClick(doc); return; }
                      onSelectDocument?.(selectedDocumentId === doc.id ? null : doc.id);
                    }}
                  >
                    <TableCell className="text-xs text-muted-foreground">{formatCreatedAt(doc.created_at)}</TableCell>
                    <TableCell className="text-xs">{renderCell(doc, "fecha", doc.fecha)}</TableCell>
                    {!showMinimal && (
                      <TableCell className="text-xs">
                    {editingId === doc.id ? (
                          <div className="w-full overflow-hidden">{renderCell(doc, "tipo_documento")}</div>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium whitespace-nowrap">
                            {doc.tipo_documento}
                          </span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-xs font-mono max-w-[110px]" onClick={(e) => e.stopPropagation()}><TruncatedCell>{renderInlineDocCell(doc, "no_documento", doc.no_documento)}</TruncatedCell></TableCell>
                    <TableCell className="text-xs" onClick={(e) => e.stopPropagation()}>
                      {editingId === doc.id ? (
                        <EmbarqueSelectField
                          value={editValues["embarque_id"] ?? ""}
                          onValueChange={(v) => setEditValues(prev => ({ ...prev, embarque_id: v }))}
                          className="h-7 text-xs w-full"
                        />
                      ) : doc.embarque_numero_do ? (
                        <Badge
                          variant="outline"
                          className="cursor-pointer text-[10px] border-primary/30 text-primary hover:bg-primary/10 max-w-full truncate inline-flex items-center"
                          onClick={() => doc.embarque_id && setEmbarqueDetailId(doc.embarque_id)}
                        >
                          <Ship className="h-3 w-3 mr-1 shrink-0" />
                          <span className="truncate">{doc.embarque_numero_do}</span>
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-[10px]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs max-w-[150px]"><TruncatedCell>{renderCell(doc, "emisor", doc.emisor)}</TruncatedCell></TableCell>
                    <TableCell className="text-xs max-w-[150px]"><TruncatedCell>{renderCell(doc, "receptor", doc.receptor)}</TruncatedCell></TableCell>
                    {!showPacking && (
                      <TableCell className="text-center"><LogisticsHoverPill doc={doc} /></TableCell>
                    )}
                    {showFinancial && (
                      <>
                        <TableCell className="text-xs">
                          {editingId === doc.id ? (
                            renderCell(doc, "moneda", doc.moneda)
                          ) : doc.moneda ? (
                            doc.moneda
                          ) : (
                            <CurrencyQuickFix
                              onSave={async (value) => handleDocInlineSave(doc, "moneda" as EditableField, value)}
                            />
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono bg-muted/30">
                          {(() => {
                            const nonFin = isNonFinancialType(doc.tipo_documento);
                            if (nonFin && !editingId) return <span className="text-muted-foreground">N/A</span>;
                            if (editingId === doc.id && editDerivedTotal) return `$${editDerivedTotal.total_factura.toLocaleString()}`;
                            // Read native total from registro_documentos. Trust the value: only "—" when truly NULL.
                            // The Edge Function populates total_factura for ALL doc types (including expense docs).
                            return doc.total_factura != null
                              ? `$${Number(doc.total_factura).toLocaleString()}`
                              : <span className="text-muted-foreground">—</span>;
                          })()}
                        </TableCell>
                        {/* TSK-205: Flete/Seguro/Impuestos/Descuento/Otros Cargos eliminados — Bolsa de Gastos Globales V2 (gastos_embarque) */}
                      </>
                    )}
                    {showPacking && (
                      <>
                        <TableCell className="text-xs text-right font-mono">
                          {Number(doc.total_piezas) > 0 ? Number(doc.total_piezas).toLocaleString() : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono">
                          {Number(doc.peso_bruto_kg) > 0 ? Number(doc.peso_bruto_kg).toLocaleString() : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono">
                          {Number(doc.volumen_cbm) > 0 ? Number(doc.volumen_cbm).toLocaleString() : "—"}
                        </TableCell>
                      </>
                    )}
                    {!showMinimal && (
                      <>
                        <TableCell className="text-xs max-w-[150px]"><TruncatedCell>{renderCell(doc, "origen", doc.origen)}</TruncatedCell></TableCell>
                        <TableCell className="text-xs max-w-[150px]"><TruncatedCell>{renderCell(doc, "destino", doc.destino)}</TruncatedCell></TableCell>
                      </>
                    )}
                    {showFinancial && (
                      <TableCell className="text-xs">{renderCell(doc, "incoterm", doc.incoterm)}</TableCell>
                    )}
                    <TableCell className="text-xs max-w-[200px] text-muted-foreground"><TruncatedCell>{renderCell(doc, "notas_tecnicas", doc.notas_tecnicas)}</TruncatedCell></TableCell>
                    {!hideAuditColumn && (
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10"
                          title="Auditar"
                          onClick={(e) => { e.stopPropagation(); setAuditDoc(doc); setAuditOpen(true); }}
                        >
                          <ClipboardCheck className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <Select
                        value={doc.estado || "Pendiente"}
                        onValueChange={(val) => updateEstado(doc.id, val as EstadoDocumento)}
                      >
                        <SelectTrigger className="h-7 w-auto border-0 bg-transparent p-0 shadow-none focus:ring-0 [&>svg]:hidden">
                          <Badge className={cn("text-[10px] cursor-pointer border", ESTADO_STYLES[doc.estado as EstadoDocumento || "Pendiente"])}>
                            {doc.estado || "Pendiente"}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {ESTADOS.map((e) => (
                            <SelectItem key={e} value={e}>
                              <span className="flex items-center gap-2">
                                <span className={cn("h-2 w-2 rounded-full", {
                                  "bg-yellow-500": e === "Pendiente",
                                  "bg-blue-500": e === "Procesando",
                                  "bg-green-500": e === "Pagado",
                                  "bg-primary": e === "Completado",
                                  "bg-destructive": e === "Cancelado",
                                })} />
                                {e}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {editingId === doc.id ? (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-500 hover:text-green-400" onClick={(e) => { e.stopPropagation(); saveEdit(); }}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); cancelEdit(); }}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={(e) => { e.stopPropagation(); startEdit(doc); }}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteId(doc.id); }}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <TablePagination
        currentPage={currentPage}
        totalItems={filtered.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <DocumentViewerModal
        open={!!viewerUrl}
        onOpenChange={(open) => { if (!open) setViewerUrl(null); }}
        url={viewerUrl || ""}
        title={viewerTitle}
      />

      <AuditPanel doc={auditDoc} open={auditOpen} onOpenChange={setAuditOpen} />

      {!disableEmbarqueDetail && (
        <EmbarqueDetailModal
          embarqueId={embarqueDetailId}
          open={!!embarqueDetailId}
          onOpenChange={(open) => { if (!open) setEmbarqueDetailId(null); }}
        />
      )}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent className="glass border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              El documento y sus ítems de inventario se ocultarán de la vista principal. Podrá ser recuperado por un administrador si es necesario.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-amber-600 text-white hover:bg-amber-700">
              Archivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
